import React, { useEffect, useRef, useState } from 'react';
import { useLongPress } from '@/hooks/useLongPress';

export const REACTIONS = ['👍', '✅', '❤️', '😂', '👀', '🙏'];

const useOutsideClose = (open, onClose) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => { if (!ref.current?.contains(e.target)) onClose(); };
    document.addEventListener('pointerdown', handler, true);
    return () => document.removeEventListener('pointerdown', handler, true);
  }, [open, onClose]);
  return ref;
};

// Six fixed quick reactions; shown on hover (desktop) or after a long-press (touch).
export const ReactionPicker = ({ onPick, onClose, messageId }) => {
  const ref = useOutsideClose(true, onClose);
  return (
    <div className="reaction-picker" role="menu" ref={ref} data-testid={`reaction-picker-${messageId}`}>
      {REACTIONS.map((emoji) => (
        <button
          type="button"
          key={emoji}
          className="reaction-option"
          onClick={(e) => { e.stopPropagation(); onPick(emoji); }}
          aria-label={`React ${emoji}`}
          data-testid={`react-${emoji}-${messageId}`}
        >
          {emoji}
        </button>
      ))}
    </div>
  );
};

const groupReactions = (reactions = []) => {
  const groups = new Map();
  reactions.forEach((r) => {
    if (!groups.has(r.emoji)) groups.set(r.emoji, []);
    groups.get(r.emoji).push(r);
  });
  return REACTIONS.filter((e) => groups.has(e)).map((emoji) => ({ emoji, people: groups.get(emoji) }));
};

const Pill = ({ emoji, people, userId, onToggle, messageId }) => {
  const [showWho, setShowWho] = useState(false);
  const mine = people.some((p) => p.user_id === userId);
  const names = people.map((p) => (p.user_id === userId ? 'You' : p.user_name || 'Teammate'));
  const longPress = useLongPress(() => setShowWho(true));
  const ref = useOutsideClose(showWho, () => setShowWho(false));
  return (
    <span className="reaction-pill-wrap" ref={ref}>
      <button
        type="button"
        className={`reaction-pill ${mine ? 'mine' : ''}`}
        title={names.join(', ')}
        aria-pressed={mine}
        aria-label={`${emoji} ${names.join(', ')}`}
        onClick={() => onToggle(emoji)}
        data-testid={`reaction-pill-${emoji}-${messageId}`}
        {...longPress}
      >
        <span>{emoji}</span>
        <span className="reaction-count">{people.length}</span>
      </button>
      {showWho && (
        <div className="seen-list reaction-who" data-testid={`reaction-who-${emoji}-${messageId}`}>
          {names.map((n, i) => <span key={`${n}-${i}`}>{n}</span>)}
        </div>
      )}
    </span>
  );
};

// Per-message pills with counts; the current user's own reactions are highlighted.
export const ReactionPills = ({ reactions, userId, onToggle, messageId }) => {
  const groups = groupReactions(reactions);
  if (!groups.length) return null;
  return (
    <div className="reaction-pills" data-testid={`reactions-${messageId}`}>
      {groups.map((g) => (
        <Pill key={g.emoji} emoji={g.emoji} people={g.people} userId={userId} onToggle={onToggle} messageId={messageId} />
      ))}
    </div>
  );
};
